import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Image, Plus, Trash2, Edit3, ArrowLeft, Shield, ToggleLeft, ToggleRight, ExternalLink, Eye, EyeOff, Loader2, X, Check, Upload, ImageIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const AdminAds = () => {
  const navigate = useNavigate();
  const { API, user, language } = useContext(AuthContext);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  const defaultForm = {
    title: '',
    description: '',
    link_url: '',
    image_url: '',
    bg_color: '#2563eb',
    text_color: '#ffffff',
    target_audience: 'all',
    is_active: true,
    priority: 1,
    starts_at: '',
    ends_at: ''
  };
  const [form, setForm] = useState(defaultForm);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchBanners();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (PNG, JPG, SVG, etc.)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be smaller than 2MB');
      return;
    }

    setImageUploading(true);
    const reader = new FileReader();
    reader.onloadend = () => {
      setForm(prev => ({ ...prev, image_url: reader.result }));
      setImageUploading(false);
      toast.success('Image uploaded!');
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
      setImageUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('helply_token');
      const res = await axios.get(`${API}/admin/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(res.data.banners || []);
    } catch (error) {
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.error('Title and description are required');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('helply_token');
      if (editingBanner) {
        await axios.put(`${API}/admin/ads/${editingBanner.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner updated successfully');
      } else {
        await axios.post(`${API}/admin/ads`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Banner created successfully');
      }
      setShowForm(false);
      setEditingBanner(null);
      setForm(defaultForm);
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title,
      description: banner.description,
      link_url: banner.link_url || '',
      image_url: banner.image_url || '',
      bg_color: banner.bg_color || '#2563eb',
      text_color: banner.text_color || '#ffffff',
      target_audience: banner.target_audience || 'all',
      is_active: banner.is_active,
      priority: banner.priority || 1,
      starts_at: banner.starts_at ? banner.starts_at.split('T')[0] : '',
      ends_at: banner.ends_at ? banner.ends_at.split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem('helply_token');
      await axios.delete(`${API}/admin/ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      const token = localStorage.getItem('helply_token');
      await axios.put(`${API}/admin/ads/${banner.id}`, { is_active: !banner.is_active }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(banner.is_active ? 'Banner deactivated' : 'Banner activated');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to update banner');
    }
  };

  const audienceLabel = (a) => {
    if (a === 'clients' || a === 'client') return { label: 'Clients', color: 'bg-blue-100 text-blue-800' };
    if (a === 'professionals' || a === 'professional') return { label: 'Professionals', color: 'bg-purple-100 text-purple-800' };
    return { label: 'All Users', color: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7 text-blue-600" />
            <div>
              <h1 className="text-xl font-bold">Ads & Marketing</h1>
              <p className="text-xs text-gray-500">Manage promotional banners for users</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Dashboard
            </Button>
            <Button size="sm" onClick={() => { setEditingBanner(null); setForm(defaultForm); setShowForm(true); }} data-testid="create-banner-btn">
              <Plus className="w-4 h-4 mr-1" />
              New Banner
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Banner Preview Info */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start gap-3">
            <Image className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">How banners work</p>
              <p className="text-sm text-blue-700 mt-1">
                Active banners appear at the top of client and professional dashboards. You can target specific user groups, set display dates, and track clicks. Banners are sorted by priority (lower number = higher priority).
              </p>
            </div>
          </div>
        </div>

        {/* Create/Edit Form */}
        {showForm && (
          <Card className="mb-6 border-2 border-blue-200" data-testid="banner-form">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{editingBanner ? 'Edit Banner' : 'Create New Banner'}</CardTitle>
                <button onClick={() => { setShowForm(false); setEditingBanner(null); setForm(defaultForm); }} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="Banner title"
                      required
                      data-testid="banner-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority (1=highest)</label>
                    <input
                      type="number"
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description / Message *</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    placeholder="Banner message or call-to-action text"
                    required
                    data-testid="banner-description-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link URL (optional)</label>
                    <input
                      type="url"
                      value={form.link_url}
                      onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Logo / Image (optional)</label>
                    {/* Upload area */}
                    <div
                      onClick={() => !imageUploading && fileInputRef.current?.click()}
                      className={`relative border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors ${imageUploading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
                      data-testid="image-upload-area"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        data-testid="image-file-input"
                      />
                      {imageUploading ? (
                        <div className="flex items-center justify-center gap-2 text-blue-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : form.image_url ? (
                        <div className="flex items-center gap-3">
                          <img
                            src={form.image_url}
                            alt="Logo preview"
                            className="w-10 h-10 rounded object-contain border border-gray-200 bg-white"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-green-700 font-medium">Image ready</p>
                            <p className="text-xs text-gray-500 truncate">Click to replace</p>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setForm(prev => ({ ...prev, image_url: '' })); }}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Remove image"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2 text-gray-500">
                          <Upload className="w-4 h-4" />
                          <span className="text-sm">Click to upload logo</span>
                          <span className="text-xs text-gray-400">(PNG, JPG, SVG · max 2MB)</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={form.bg_color} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                      <input type="text" value={form.bg_color} onChange={(e) => setForm({ ...form, bg_color: e.target.value })} className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="w-10 h-10 rounded border cursor-pointer" />
                      <input type="text" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} className="flex-1 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                    <select
                      value={form.target_audience}
                      onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="all">All Users</option>
                      <option value="client">Clients</option>
                      <option value="professional">Professionals</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, is_active: !form.is_active })}
                      className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium border transition-colors ${form.is_active ? 'bg-green-50 border-green-300 text-green-700' : 'bg-gray-50 border-gray-300 text-gray-600'}`}
                    >
                      {form.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {form.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date (optional)</label>
                    <input
                      type="date"
                      value={form.starts_at}
                      onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date (optional)</label>
                    <input
                      type="date"
                      value={form.ends_at}
                      onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {/* Live Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Live Preview</label>
                  <div
                    className="py-3 px-4 rounded-lg flex items-center justify-between gap-4"
                    style={{ backgroundColor: form.bg_color, color: form.text_color }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {form.image_url && <img src={form.image_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />}
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{form.title || 'Banner Title'}</p>
                        <p className="text-xs opacity-90 truncate">{form.description || 'Banner description'}</p>
                      </div>
                    </div>
                    {form.link_url && (
                      <span className="text-xs font-medium underline opacity-90 flex-shrink-0">Learn more</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={saving} data-testid="save-banner-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                    {editingBanner ? 'Update Banner' : 'Create Banner'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingBanner(null); setForm(defaultForm); }}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Banners List */}
        {banners.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No banners yet</h3>
            <p className="text-gray-500 mb-6">Create your first promotional banner to engage users</p>
            <Button onClick={() => setShowForm(true)} data-testid="empty-create-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create First Banner
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-base font-semibold text-gray-700">{banners.length} Banner{banners.length !== 1 ? 's' : ''}</h2>
            {banners.map((banner) => {
              const aud = audienceLabel(banner.target_audience);
              return (
                <Card key={banner.id} className={`transition-all ${!banner.is_active ? 'opacity-60' : ''}`} data-testid={`banner-card-${banner.id}`}>
                  <CardContent className="pt-4 pb-4">
                    {/* Banner Preview */}
                    <div
                      className="py-2.5 px-4 rounded-lg flex items-center justify-between gap-3 mb-4"
                      style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {banner.image_url && <img src={banner.image_url} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" onError={(e) => e.target.style.display = 'none'} />}
                        <div className="min-w-0">
                          <p className="font-semibold text-sm">{banner.title}</p>
                          <p className="text-xs opacity-90">{banner.description}</p>
                        </div>
                      </div>
                      {banner.link_url && (
                        <span className="text-xs underline opacity-90 flex-shrink-0">Learn more</span>
                      )}
                    </div>

                    {/* Banner Info */}
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${aud.color}`}>{aud.label}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${banner.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        {banner.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span className="text-xs text-gray-500">Priority: {banner.priority}</span>
                      <span className="text-xs text-gray-500">{banner.click_count || 0} clicks</span>
                      {banner.ends_at && (
                        <span className="text-xs text-gray-500">Ends: {new Date(banner.ends_at).toLocaleDateString()}</span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">by {banner.created_by_name}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={() => handleToggleActive(banner)}
                        className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${banner.is_active ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' : 'bg-green-50 hover:bg-green-100 text-green-700'}`}
                      >
                        {banner.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        {banner.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleEdit(banner)}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 transition-colors"
                        data-testid={`edit-banner-${banner.id}`}
                      >
                        <Edit3 className="w-3 h-3" />
                        Edit
                      </button>
                      {banner.link_url && (
                        <a href={banner.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors">
                          <ExternalLink className="w-3 h-3" />
                          Preview Link
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(banner.id)}
                        disabled={deletingId === banner.id}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg font-medium bg-red-50 hover:bg-red-100 text-red-700 transition-colors ml-auto disabled:opacity-50"
                        data-testid={`delete-banner-${banner.id}`}
                      >
                        {deletingId === banner.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        Delete
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAds;
